/**
 * SandboxLaunchModal — starts a new sandbox run against the current
 * draft/live workflow with a fake subject and optional context blob.
 *
 * On success redirects to /admin/workflow-engine/sandbox/{runId}.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Beaker, Sparkles, X } from "lucide-react";

function randomSubjectId() {
  return `sbx_${Math.random().toString(36).slice(2, 10)}`;
}

export default function SandboxLaunchModal({ open, onClose, definition }) {
  const navigate = useNavigate();
  const [subjectType, setSubjectType] = useState("patient");
  const [subjectId, setSubjectId] = useState(randomSubjectId());
  const [contextText, setContextText] = useState('{\n  "score": 75,\n  "tier": "premium"\n}');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSubjectType("patient");
      setSubjectId(randomSubjectId());
      setContextText('{\n  "score": 75,\n  "tier": "premium"\n}');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    let context = {};
    try {
      context = JSON.parse(contextText || "{}");
    } catch {
      toast.error("Context isn't valid JSON. Fix or leave empty.");
      return;
    }
    if (!subjectId.trim()) {
      toast.error("Subject id is required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/wfe/runs", {
        workflow_definition_id: definition.id,
        subject_type: subjectType.trim() || "patient",
        subject_id: subjectId.trim(),
        context,
        is_sandbox: true,
      });
      toast.success(`Sandbox run started · ${r.data.id}`);
      onClose?.();
      navigate(`/admin/workflow-engine/sandbox/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (!open || !definition) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="sandbox-launch-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600">
              <Beaker className="w-3 h-3" /> Sandbox
            </div>
            <h2 className="mt-1 font-display text-xl font-bold text-slate-900">
              Test-run this workflow
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Starts an isolated run against <strong>{definition.name}</strong> flagged as sandbox.
              Excluded from production analytics.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="sandbox-launch-close" className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Subject type</label>
              <input
                data-testid="sandbox-launch-subject-type"
                value={subjectType}
                onChange={(e) => setSubjectType(e.target.value)}
                placeholder="patient"
                className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Subject id</label>
              <input
                data-testid="sandbox-launch-subject-id"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                placeholder="sbx_xxxx"
                className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Initial context (JSON)</label>
            <textarea
              data-testid="sandbox-launch-context"
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
              rows={5}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-xs font-mono focus:border-blue-500 outline-none resize-none"
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Keys referenced in condition expressions read from here (e.g. <code>context.score</code>).
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm font-semibold text-slate-700 px-3 py-2 rounded-full hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="sandbox-launch-submit"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            <Sparkles className="w-3.5 h-3.5" /> {submitting ? "Starting…" : "Start sandbox run"}
          </button>
        </div>
      </form>
    </div>
  );
}
